import {
  ESTADO_CONTACTO,
  agruparClientesPorEmpresa,
  determinarEstadoContacto,
  normalizarEmpresa,
} from "../../../utils/empresaContacto";

const LABEL_FILA_FIJO = { OTROS: "Otros" };

// Nombre de categoría TAL CUAL está guardado (2026-09-24, 11ª sesión, punto 2 del pedido de
// Ignacio) — reemplaza al viejo `tituloDesdeCategoria` (lowercase + Título Case por palabra:
// "BANCA & FINANZAS" → "Banca & Finanzas"), que ROMPÍA cualquier sigla: "RRHH" → "Rrhh", "FMCG |
// CONSUMER GOODS" → "Fmcg | Consumer Goods" — no eran casos aislados, un Título Case genérico no
// sabe qué palabra es sigla. Verificado contra las 49 filas reales de `categorias` (`SELECT
// nombre FROM categorias`): TODAS ya vienen bien guardadas en mayúsculas ("BANCA & FINANZAS",
// "AEROLÍNEAS", "RRHH", "FMCG | CONSUMER GOODS") — el dato de origen está correcto, el que rompía
// era el frontend. No se toca la base (10.13) — el fix es sacar la transformación de acá. Sigue
// exportada (6ª sesión) — `index.js` la reusa para las etiquetas del dropdown de filtro de
// Categoría, una sola fuente para el label (10.1).
export const etiquetaCategoria = (nombre) => nombre || "";

const labelDeFila = (filaKey) => LABEL_FILA_FIJO[filaKey] || etiquetaCategoria(filaKey);

/**
 * Resuelve a qué fila del mapa pertenece una fila de `clientes` (granulada por marca). Regla
 * única: la categoría la aporta la FILA — SIEMPRE exactamente una fila (2026-09-23, 6ª sesión: se
 * retira VIP, que hasta la sesión anterior podía sumar una 2ª fila por empresa).
 *   - Va a su categoría si está entre las activas; si no (categoría nula/sin match), a 'OTROS'.
 * @param {string|null} categoriaNombre
 * @param {Set<string>} categoriasActivasSet - ver `calcularCategoriasActivas`
 * @returns {string[]} array de 1 elemento — se mantiene el shape array por los `.forEach` que lo
 * consumen, no porque una fila pueda pertenecer a más de una fila del mapa
 */
export const resolverFilasDeClienteRow = (categoriaNombre, categoriasActivasSet) =>
  categoriasActivasSet.has(categoriaNombre) ? [categoriaNombre] : ["OTROS"];

/**
 * Categorías con fila propia (2026-09-23, 4ª sesión, pedido de Ignacio: "TODAS las categorías
 * activas, sin tope" — retira el techo `MAX_CATEGORIAS_VISIBLES` de la sesión anterior, que
 * recortaba a un ranking por monto). Activa = tiene cierres en el período O al menos una empresa
 * asignada (`clientes.categoria`) — en la práctica casi siempre el segundo caso ya cubre al
 * primero (un cierre siempre viene de un client-row, que ya aporta la categoría), pero se computa
 * como unión explícita para no asumirlo. Sin ranking ni corte: una categoría sin ningún cierre en
 * el período queda con fila propia en Evolución (vacía, en gris) y con información real en
 * Cobertura — deliberado, es lo que antes quedaba escondido dentro de Otros.
 * @returns {Set<string>} categorías reales con fila propia
 */
const calcularCategoriasActivas = ({ clientes, oportunidades, categoriasPorId, clientesPorId, mesesSet }) => {
  const activas = new Set();
  (clientes || []).forEach((c) => {
    const nombre = categoriasPorId.get(c.categoria);
    if (nombre) activas.add(nombre);
  });
  (oportunidades || []).forEach((op) => {
    if (!esCierre(op) || !mesesSet.has(op.mes_cierre)) return;
    const clienteRow = clientesPorId.get(op.client_id);
    const nombre = clienteRow && categoriasPorId.get(clienteRow.categoria);
    if (nombre) activas.add(nombre);
  });
  return activas;
};

/** Arma, para cada fila del mapa, el array de filas de `clientes` que le pertenecen. */
const construirMembresiaPorFila = (clientes, categoriasPorId, ordenFilasBase, categoriasActivasSet) => {
  const porFila = new Map(ordenFilasBase.map((f) => [f, []]));
  clientes.forEach((c) => {
    const categoriaNombre = categoriasPorId.get(c.categoria) || null;
    resolverFilasDeClienteRow(categoriaNombre, categoriasActivasSet).forEach((filaKey) => porFila.get(filaKey).push(c));
  });
  return porFila;
};

const esCierre = (op) => op.estatus === "100%";

/**
 * Celdas + total a partir de una lista de eventos normalizados `{mes, monto, empresaNorm,
 * empresaDisplay}` (2026-09-24, 17ª sesión, pedido de Ignacio) — usada para la fila fantasma del
 * Año Anterior Y (2026-09-26, 20ª sesión, punto A del pedido de Ignacio) también para armar las
 * filas del OTRO país en modo comparación, vía una segunda llamada completa a
 * `construirDatosCategorias` (ver comentario ahí) — en los dos casos es la MISMA forma de celda
 * que ya arman `armarFila`/`armarFilaFacturacion` para el período real (con desglose `.empresas`
 * por mes, para que el tooltip de la fila fantasma sea IGUAL al de la fila real). El período real
 * de la fila propia no pasa por acá — queda intacto en `armarFila`/`armarFilaFacturacion`, sin
 * tocar código que ya funciona (10.14).
 * @param {Array<{mes:string, monto:number, empresaNorm:string, empresaDisplay:string}>} eventos
 * @param {string[]} meses - claves "MM-YYYY" del período, orden cronológico
 */
const construirCeldasDesdeEventos = (eventos, meses) => {
  const celdasPorMes = new Map(meses.map((m) => [m, 0]));
  const empresasPorMes = new Map(meses.map((m) => [m, new Map()]));
  eventos.forEach(({ mes, monto, empresaNorm, empresaDisplay }) => {
    celdasPorMes.set(mes, (celdasPorMes.get(mes) || 0) + monto);
    if (empresaNorm) {
      const mapaDelMes = empresasPorMes.get(mes);
      if (mapaDelMes) {
        const prev = mapaDelMes.get(empresaNorm) || { empresaNorm, empresaDisplay, monto: 0 };
        prev.monto += monto;
        mapaDelMes.set(empresaNorm, prev);
      }
    }
  });
  const celdas = meses.map((mes) => ({
    mes,
    monto: celdasPorMes.get(mes) || 0,
    empresas: [...empresasPorMes.get(mes).values()].sort((a, b) => b.monto - a.monto),
  }));
  const totalPeriodo = celdas.reduce((acc, c) => acc + c.monto, 0);
  return { celdas, totalPeriodo };
};

/**
 * Variación % del período real contra el AA (2026-09-24, 17ª sesión, punto 1 del pedido de
 * Ignacio) — `null` cuando el AA fue cero: no hay con qué comparar ("—" en `index.js`), evita una
 * división por cero y un falso "+∞%"/"-100%" engañoso. Un solo lugar (10.1) — Cierres y
 * Facturación llaman a esto con SU PROPIO total/totalAA (punto 3: "cada una con su propio dato"),
 * nunca se reimplementa la fórmula inline.
 * @param {number} totalPeriodo
 * @param {number} totalPeriodoAA
 * @returns {number|null}
 */
const calcularVariacionPct = (totalPeriodo, totalPeriodoAA) =>
  totalPeriodoAA > 0 ? ((totalPeriodo - totalPeriodoAA) / totalPeriodoAA) * 100 : null;

/**
 * Núcleo de la vista Categorías: arma los datos de los dos paneles (evolución + cobertura) a
 * partir de datos YA cargados (cargarOportunidadesVentas + getActividadPrecalculada) — sin
 * queries nuevas, sin recalcular fuentes ajenas (10.1/10.27).
 *
 * @param {object} args
 * @param {Array} args.clientes - filas de `clientes` de UN país (no eliminadas) — SIEMPRE un solo
 * país por llamada (2026-09-26, 20ª sesión, pedido de Ignacio: se retira el modo consolidado que
 * unía Chile+Perú en un solo array). El modo comparación ("Ver Perú"/"Ver Chile", `index.js`)
 * llama a esta función DOS VECES, una por país, y arma la fila fantasma del otro país buscando por
 * `filaKey` en el segundo resultado — esta función no sabe nada de comparación ni de un segundo
 * país, sigue siendo de un solo país de punta a punta
 * @param {Array} args.oportunidades - de cargarOportunidadesVentas (sales+contratos+btl), del
 * MISMO país que `clientes`
 * @param {Array} args.categorias - [{id, nombre}]
 * @param {Set<number>} args.clienteIdsConActividadEnPeriodo - cliente_id con actividad CRM dentro
 * de `mesesDelPeriodo` (11ª sesión — antes ventana fija de 90 días, independiente del período)
 * @param {string[]} args.mesesDelPeriodo - 12 claves "MM-YYYY", orden cronológico
 * @param {string[]} args.mesesDelPeriodoAA - 12 claves "MM-YYYY" del MISMO período, un año antes
 * (2026-09-24, 17ª sesión, punto 0/3 del pedido de Ignacio — `getMesesDelPeriodoAA`,
 * `periodoUtil.js`), para la columna de variación % y la fila fantasma
 * @param {(op: object) => number} args.getImporteLocal - conversor de moneda (10.185)
 * @param {string[]} [args.filtroCategorias] - `categorias.nombre` elegidas ([] = todas)
 * @param {string[]} [args.filtroEmpresas] - `empresaNorm` elegidas ([] = todas)
 * @returns {{filasEvolucion: Array, filasFacturacion: Array, filasCobertura: Array, categoriasDisponibles: string[]}}
 */
export const construirDatosCategorias = ({
  clientes,
  oportunidades,
  categorias,
  clienteIdsConActividadEnPeriodo,
  mesesDelPeriodo,
  mesesDelPeriodoAA,
  getImporteLocal,
  filtroCategorias = [],
  filtroEmpresas = [],
}) => {
  const categoriasPorId = new Map((categorias || []).map((c) => [c.id, c.nombre]));
  const clientesPorId = new Map((clientes || []).map((c) => [c.id, c]));
  const mesesSet = new Set(mesesDelPeriodo);
  const mesesSetAA = new Set(mesesDelPeriodoAA);

  // Universo COMPLETO de categorías activas, SIN el filtro de empresa — opciones del dropdown de
  // Categoría (2026-09-23, 6ª sesión): no se achica si el usuario ya filtró por empresa, para
  // poder elegir cualquier categoría real en cualquier momento sin tener que destildar Empresa
  // primero. Alfabético — es un listado para buscar un nombre, no un ranking por monto.
  const categoriasDisponibles = [
    ...calcularCategoriasActivas({ clientes, oportunidades, categoriasPorId, clientesPorId, mesesSet }),
  ].sort((a, b) => a.localeCompare(b));

  // Filtro de EMPRESA (2026-09-23, 6ª sesión, punto 2/3 del pedido de Ignacio): acota
  // `clientes`/`oportunidades` a las empresas elegidas ANTES de correr el resto del pipeline —
  // todo lo que sigue (categorías activas, montos, celdas, chips, y el denominador de cada barra
  // de Cobertura) deriva de este subconjunto sin ningún criterio aparte, así que "se acotan los
  // datos de la tabla" y "el % de Cobertura no miente" salen GRATIS de una sola decisión de
  // diseño — no hace falta recortar el denominador de Cobertura a mano más abajo. Vacío = sin
  // filtro = todo, como hoy.
  const filtroEmpresasSet = new Set(filtroEmpresas);
  const clientesFiltrados =
    filtroEmpresasSet.size === 0
      ? clientes || []
      : (clientes || []).filter((c) => filtroEmpresasSet.has(normalizarEmpresa(c.empresa)));
  const clienteIdsFiltradosSet = new Set(clientesFiltrados.map((c) => c.id));
  const oportunidadesFiltradas =
    filtroEmpresasSet.size === 0
      ? oportunidades || []
      : (oportunidades || []).filter((op) => clienteIdsFiltradosSet.has(op.client_id));

  const categoriasActivasSet = calcularCategoriasActivas({
    clientes: clientesFiltrados,
    oportunidades: oportunidadesFiltradas,
    categoriasPorId,
    clientesPorId,
    mesesSet,
  });
  const ordenFilasBase = [...categoriasActivasSet, "OTROS"];

  const membresiaPorFila = construirMembresiaPorFila(
    clientesFiltrados,
    categoriasPorId,
    ordenFilasBase,
    categoriasActivasSet
  );

  // ── Panel de evolución (tira de calor) ────────────────────────────────────────────────────
  const oportunidadesPorFila = new Map(ordenFilasBase.map((f) => [f, []]));
  oportunidadesFiltradas.forEach((op) => {
    if (!esCierre(op) || !mesesSet.has(op.mes_cierre)) return;
    const clienteRow = clientesPorId.get(op.client_id);
    if (!clienteRow) return;
    const categoriaNombre = categoriasPorId.get(clienteRow.categoria) || null;
    const empresaNorm = normalizarEmpresa(clienteRow.empresa);
    const empresaDisplay = (clienteRow.empresa || "").trim();
    resolverFilasDeClienteRow(categoriaNombre, categoriasActivasSet).forEach((filaKey) =>
      oportunidadesPorFila.get(filaKey).push({ op, empresaNorm, empresaDisplay })
    );
  });

  // ── Año Anterior (AA) — mismo período, un año antes (2026-09-24, 17ª sesión, puntos 0/1/3 del
  // pedido de Ignacio) ────────────────────────────────────────────────────────────────────────
  // Eventos de CIERRE del período AA, normalizados a `{mes, monto, empresaNorm, empresaDisplay}`
  // — MISMA fuente (`oportunidadesFiltradas`, ya trae todo el histórico, sin queries nuevas) y
  // MISMO criterio de cierre (`esCierre`) que `oportunidadesPorFila` arriba, solo cambia el
  // filtro de mes (`mesesSetAA` en vez de `mesesSet`). Deliberadamente NO se toca
  // `oportunidadesPorFila` — de ahí cuelgan `estadoPorEmpresaPorFila`/activeness/`ultimoAnoCierre`,
  // ninguno de los cuales lleva reloj AA (punto 4: el criterio de la fila ámbar no cambia).
  const eventosCierreAA = [];
  oportunidadesFiltradas.forEach((op) => {
    if (!esCierre(op) || !mesesSetAA.has(op.mes_cierre)) return;
    const clienteRow = clientesPorId.get(op.client_id);
    if (!clienteRow) return;
    eventosCierreAA.push({
      categoriaNombre: categoriasPorId.get(clienteRow.categoria) || null,
      mes: op.mes_cierre,
      monto: getImporteLocal(op),
      empresaNorm: normalizarEmpresa(clienteRow.empresa),
      empresaDisplay: (clienteRow.empresa || "").trim(),
    });
  });
  const eventosCierreAAPorFila = new Map(ordenFilasBase.map((f) => [f, []]));
  eventosCierreAA.forEach((ev) =>
    resolverFilasDeClienteRow(ev.categoriaNombre, categoriasActivasSet).forEach((filaKey) =>
      eventosCierreAAPorFila.get(filaKey).push(ev)
    )
  );

  // Estado de contacto POR FILA, no global (2026-09-23, 3ª sesión — corrige el descuadre real
  // encontrado en Bebidas/Chile: ABINBEV cerró un contrato EN Bebidas → 1 empresa/100% de
  // concentración en la columna "Empresas" de Evolución, pero NESTLÉ y UNILEVER aparecían igual
  // como chips "Activadas" ahí — cerraron en 2026, pero en FMCG | CONSUMER GOODS vía otra marca
  // suya, Nespresso/Dove, no en Bebidas — verificado con datos reales, `clientes`+`sales`+
  // `sales_contratos`). Antes `estadoPorEmpresa` era UN SOLO Map global, construido sobre TODAS
  // las filas-marca de la empresa sin importar a qué fila del mapa pertenecen — el mismo criterio
  // alimentaba a la vez los chips de Evolución Y el conteo "activado" de Cobertura, así que las
  // dos superficies COINCIDÍAN entre sí, pero ninguna coincidía con la columna "Empresas" (que
  // siempre fue fila-scoped, ligada a `montoPorEmpresa`/Concentración). Pedido de Ignacio: llevar
  // el criterio de "activado" al nivel de la FILA — cerró EN esa categoría, no en cualquier lado
  // — y que Evolución y Cobertura lean del MISMO Map por fila, para no volver a contar cosas
  // distintas entre los dos paneles.
  //
  // Cobertura: UN SOLO RELOJ (2026-09-24, 11ª sesión, punto 1 del pedido de Ignacio) — hasta esta
  // sesión "vinculado" miraba una ventana FIJA de 90 días desde HOY (`DIAS_VINCULADO`, `index.js`,
  // retirada), un reloj DISTINTO al que ya usaba "activado" (`mesesSet`, el período elegido
  // arriba). Los tres estados de la planilla original NO cambian (le vendimos / estamos en
  // contacto / ninguna de las dos); lo que cambia es la ventana: ahora "estamos en contacto" mira
  // el MISMO `mesesDelPeriodo` que "le vendimos" — `clienteIdsConActividadEnPeriodo` ya viene
  // re-derivado por período desde `index.js` (`construirClienteIdsConActividadEnPeriodo`), acá
  // solo se consume, sin recalcular la ventana. El universo de la fila (`gruposEmpresaFila`, de
  // dónde sale `total`, el denominador) NO lleva reloj — es deliberado, ver "Vista Cobertura —
  // detalle" en la ficha.
  const estadoPorEmpresaPorFila = new Map();
  ordenFilasBase.forEach((filaKey) => {
    const empresasConCierreEnFila = new Set(
      oportunidadesPorFila
        .get(filaKey)
        .map(({ empresaNorm }) => empresaNorm)
        .filter(Boolean)
    );
    const gruposEmpresaFila = agruparClientesPorEmpresa(membresiaPorFila.get(filaKey));
    const mapaFila = new Map();
    gruposEmpresaFila.forEach((grupo, empresaNorm) => {
      const tieneVentaCerradaEnPeriodo = empresasConCierreEnFila.has(empresaNorm);
      const tieneActividadEnPeriodo = grupo.clienteIds.some((id) => clienteIdsConActividadEnPeriodo.has(id));
      mapaFila.set(
        empresaNorm,
        determinarEstadoContacto({
          tieneVentaCerradaEnPeriodo,
          tieneActividadReciente: tieneActividadEnPeriodo,
        })
      );
    });
    estadoPorEmpresaPorFila.set(filaKey, mapaFila);
  });

  // Fila ámbar — categorías sin cierres (2026-09-24, 9ª sesión, punto 3 del pedido de Ignacio;
  // criterio ampliado en la 15ª sesión). Hasta la 14ª sesión, `ultimoAnoCierre` (el año MÁS
  // RECIENTE en que la fila cerró algo, alguna vez) era también el GATE de `enfriada` — una
  // categoría que NUNCA vendió nada quedaba afuera del ámbar. Pedido de Ignacio, 15ª sesión: el
  // ámbar pasa a TODA categoría sin cierres en el período elegido, haya vendido antes o nunca —
  // el gate se retira (ver `enfriada` más abajo), `ultimoAnoCierrePorFila` se sigue calculando
  // igual (recorre TODO el histórico de `oportunidadesFiltradas`, sin acotar a `mesesSet` —
  // `getOportunidades`/`getContratos` no filtran por año, 10.114, así que el array YA trae todo el
  // histórico del país sin queries nuevas) pero ahora es SOLO el dato del tooltip, ya no decide si
  // la fila se pinta. "Otros" queda afuera (no es una categoría real).
  const ultimoAnoCierrePorFila = new Map();
  oportunidadesFiltradas.forEach((op) => {
    if (!esCierre(op) || !op.mes_cierre) return;
    const clienteRow = clientesPorId.get(op.client_id);
    if (!clienteRow) return;
    const categoriaNombre = categoriasPorId.get(clienteRow.categoria) || null;
    const anoCierre = parseInt(op.mes_cierre.split("-")[1], 10);
    if (!anoCierre) return;
    resolverFilasDeClienteRow(categoriaNombre, categoriasActivasSet).forEach((filaKey) => {
      if (filaKey === "OTROS") return;
      const previo = ultimoAnoCierrePorFila.get(filaKey) || 0;
      if (anoCierre > previo) ultimoAnoCierrePorFila.set(filaKey, anoCierre);
    });
  });

  const armarFila = (filaKey, opsDeLaFila, clientesDeLaFila, eventosAA) => {
    const estadoPorEmpresa = estadoPorEmpresaPorFila.get(filaKey);
    const celdasPorMes = new Map(mesesDelPeriodo.map((m) => [m, 0]));
    const montoPorEmpresa = new Map();
    // Empresa × mes (2026-09-24, 8ª sesión, pedido de Ignacio — el tooltip de la celda tiene que
    // decir QUIÉN) — MISMA agrupación por empresa que `montoPorEmpresa`/`agruparClientesPorEmpresa`
    // (10.276), pero cortada por mes en vez de acumulada en todo el período: `celda.empresas` abajo
    // sale de acá, una `Map<empresaNorm, {…}>` por mes.
    const empresasPorMes = new Map(mesesDelPeriodo.map((m) => [m, new Map()]));
    opsDeLaFila.forEach(({ op, empresaNorm, empresaDisplay }) => {
      const monto = getImporteLocal(op);
      celdasPorMes.set(op.mes_cierre, (celdasPorMes.get(op.mes_cierre) || 0) + monto);
      if (empresaNorm) {
        const prev = montoPorEmpresa.get(empresaNorm) || { empresaDisplay, monto: 0 };
        prev.monto += monto;
        montoPorEmpresa.set(empresaNorm, prev);

        const mapaDelMes = empresasPorMes.get(op.mes_cierre);
        if (mapaDelMes) {
          const prevMes = mapaDelMes.get(empresaNorm) || { empresaNorm, empresaDisplay, monto: 0 };
          prevMes.monto += monto;
          mapaDelMes.set(empresaNorm, prevMes);
        }
      }
    });
    // Celda de Evolución (punto 1, 2026-09-23, 3ª sesión): muestra el MONTO abreviado ("12,4M"),
    // no la cantidad de empresas — ese conteo ya vive en la columna "Empresas" (`total` abajo), no
    // hace falta repetirlo por celda. `empresas` (2026-09-24, 8ª sesión) — desglose de ESE mes,
    // mayor a menor por monto, consumido por el tooltip (`index.js`).
    const celdas = mesesDelPeriodo.map((mes) => ({
      mes,
      monto: celdasPorMes.get(mes) || 0,
      empresas: [...empresasPorMes.get(mes).values()].sort((a, b) => b.monto - a.monto),
    }));
    const totalPeriodo = celdas.reduce((acc, c) => acc + c.monto, 0);

    // Columna de empresas (2026-09-23) — MISMA agrupación (`agruparClientesPorEmpresa`) y MISMO
    // estado POR FILA (`estadoPorEmpresaPorFila.get(filaKey)`, vía `determinarEstadoContacto`) que
    // usa el panel de Cobertura para esta fila — no se inventa un criterio propio (3ª sesión,
    // 2026-09-23: antes era un Map GLOBAL por empresa, ver comentario junto a
    // `estadoPorEmpresaPorFila` más arriba). Activadas = estado 'activado' EN ESTA FILA (cerró
    // categorizado acá, no en cualquier lado), ordenadas por el monto que aportaron — ÚNICA lista
    // de chips desde la 7ª sesión (Pendientes se retiró de la UI, ver historial en la ficha).
    // `total` = TODAS las empresas asignadas a la fila, activas o no en el período — universo
    // estable (no depende de `mesesDelPeriodo`, ver "Universo de Cobertura" en la ficha). MISMO
    // nombre de campo que ya usaba `filasCobertura` para su denominador del % y de la barra — 7ª
    // sesión, punto 3: columnas unificadas, ahora también unifica el campo por debajo.
    const gruposEmpresaFila = agruparClientesPorEmpresa(clientesDeLaFila);
    const empresasActivadas = [];
    gruposEmpresaFila.forEach((grupo, empresaNorm) => {
      const estado = estadoPorEmpresa.get(empresaNorm) || ESTADO_CONTACTO.SIN_CONTACTO;
      if (estado === ESTADO_CONTACTO.ACTIVADO) {
        empresasActivadas.push({
          empresaNorm,
          empresaDisplay: grupo.empresaDisplay,
          monto: montoPorEmpresa.get(empresaNorm)?.monto || 0,
        });
      }
    });
    empresasActivadas.sort((a, b) => b.monto - a.monto);

    // Fila ámbar (2026-09-24, 9ª sesión; criterio ampliado en la 15ª) — `opsDeLaFila` son los
    // cierres YA acotados a `mesesSet` (ver `oportunidadesPorFila` arriba): si hay al menos uno,
    // la fila está "caliente" y nunca se pinta, sin importar su historial. Si NO hay ninguno, se
    // pinta SIEMPRE (15ª sesión — antes exigía además `ultimoAnoCierre` truthy, es decir, haber
    // vendido en algún año anterior; una categoría que nunca vendió nada quedaba afuera del
    // ámbar). `ultimoAnoCierre` (`null` si nunca vendió) ya no decide la fila — queda solo como
    // dato para el tooltip (`index.js`: "Último cierre en <año>" vs. "Sin cierres registrados").
    const ultimoAnoCierre = ultimoAnoCierrePorFila.get(filaKey) || null;
    const enfriada = filaKey !== "OTROS" && opsDeLaFila.length === 0;

    // Año Anterior (AA) — 17ª sesión, puntos 1/2/3 del pedido de Ignacio: `celdasAA`/`totalPeriodoAA`
    // alimentan la fila fantasma ("Ver AA"), `variacionPct` alimenta la columna nueva. Punto 4: el
    // criterio de `enfriada` arriba NO mira nada de esto — una categoría ámbar que vendió el año
    // pasado da `variacionPct = -100` (mismo hecho, dos lecturas), sin acoplar los dos cálculos.
    const { celdas: celdasAA, totalPeriodo: totalPeriodoAA } = construirCeldasDesdeEventos(
      eventosAA,
      mesesDelPeriodoAA
    );
    const variacionPct = calcularVariacionPct(totalPeriodo, totalPeriodoAA);

    return {
      filaKey,
      label: labelDeFila(filaKey),
      celdas,
      totalPeriodo,
      total: gruposEmpresaFila.size,
      empresasActivadas,
      enfriada,
      ultimoAnoCierre,
      celdasAA,
      totalPeriodoAA,
      variacionPct,
    };
  };

  const filasArmadas = ordenFilasBase.map((filaKey) =>
    armarFila(
      filaKey,
      oportunidadesPorFila.get(filaKey),
      membresiaPorFila.get(filaKey),
      eventosCierreAAPorFila.get(filaKey)
    )
  );
  // Lookup por filaKey — reusado por `filasCobertura` de abajo para NO reimplementar el cálculo de
  // `totalPeriodo`/`total`/`empresasActivadas` (ver comentario junto a `filasCobertura`).
  const filasArmadasPorKey = new Map(filasArmadas.map((f) => [f.filaKey, f]));

  // Orden fijo (2026-09-23, pedido de Ignacio): las categorías activas (`categoriasActivasSet`,
  // TODAS, sin tope desde la 4ª sesión) por monto descendente, después Otros — Otros NUNCA se
  // ordena por monto contra las categorías (VIP existía hasta la 5ª sesión, retirado en la 6ª —
  // ver "VIP se elimina" en la ficha). El orden final en pantalla (Otros último, categorías
  // ordenables por el usuario) lo arma `index.js` — este orden es solo el DEFAULT que sale de acá.
  const filasCategorias = filasArmadas
    .filter((f) => f.filaKey !== "OTROS")
    .sort((a, b) => b.totalPeriodo - a.totalPeriodo);
  const filaOtros = filasArmadas.find((f) => f.filaKey === "OTROS");
  const filasEvolucion = [...filasCategorias, filaOtros].filter(Boolean);

  // ── Panel de cobertura ─────────────────────────────────────────────────────────────────────
  const filasCobertura = ordenFilasBase.map((filaKey) => {
    const clientesDeLaFila = membresiaPorFila.get(filaKey);
    const estadoPorEmpresa = estadoPorEmpresaPorFila.get(filaKey);
    const gruposEmpresaFila = agruparClientesPorEmpresa(clientesDeLaFila);
    const porEstado = { activado: [], vinculado: [], sin_contacto: [] };
    gruposEmpresaFila.forEach((grupo, empresaNorm) => {
      const estado = estadoPorEmpresa.get(empresaNorm) || ESTADO_CONTACTO.SIN_CONTACTO;
      porEstado[estado].push(grupo.empresaDisplay);
    });
    Object.values(porEstado).forEach((arr) => arr.sort((a, b) => a.localeCompare(b)));
    // Reusa `armarFila` para `totalPeriodo`/`total`/`empresasActivadas`/`enfriada`/
    // `ultimoAnoCierre`/`variacionPct` (nace en el fix del bug real de la 5ª sesión —
    // `filasCobertura` se armaba en un `.map` separado de `armarFila` y no tenía chips; se
    // corrigió reusando el resultado en vez de reimplementarlo). 7ª sesión, punto 3: `total`
    // (denominador del % y de la barra) es el MISMO campo que ahora también muestra la columna
    // Empresas en Evolución — las dos vistas cuentan exactamente lo mismo. 9ª sesión: mismo
    // criterio para el punto ámbar. 17ª sesión: `variacionPct` se suma a la lista de campos
    // reusados — Cobertura no tiene concepto propio de "cerrado"/"facturado", así que su columna
    // de variación reusa la de Cierres, mismo patrón que ya reusa `totalPeriodo`.
    const { totalPeriodo, total, empresasActivadas, enfriada, ultimoAnoCierre, variacionPct } =
      filasArmadasPorKey.get(filaKey);
    return {
      filaKey,
      label: labelDeFila(filaKey),
      total,
      totalPeriodo,
      activado: porEstado.activado.length,
      vinculado: porEstado.vinculado.length,
      sinContacto: porEstado.sin_contacto.length,
      activadoPct: total > 0 ? (porEstado.activado.length / total) * 100 : 0,
      nombresPorEstado: porEstado,
      empresasActivadas,
      enfriada,
      variacionPct,
      ultimoAnoCierre,
    };
  });

  // Mismo orden que la evolución — las dos filas de una misma categoría quedan a la misma altura
  // en los dos paneles.
  const ordenFilas = filasEvolucion.map((f) => f.filaKey);
  filasCobertura.sort((a, b) => ordenFilas.indexOf(a.filaKey) - ordenFilas.indexOf(b.filaKey));

  // ── Panel de facturación (2026-09-24, 9ª sesión, punto 2 del pedido de Ignacio) ──────────────
  // Punto 1 verificado con queries reales antes de construir esto (`docs/decisiones/2026-09-24-
  // categorias-facturacion-verificacion.md`): `mes_factura` cubre 99,4% de los cierres de Chile y
  // 100% de Perú (2026 y todo el histórico) — muy por encima de `facturafecha`, cuya cobertura es
  // mala (regla 10.26); no hace falta sumar `facturas_sii_emitidas`. Para `sales_contratos` la
  // facturación es POR CUOTA dentro de `meses[]` (punto 1b) — 100% de las cuotas de contratos
  // cerrados tienen `mes`+`importe` poblados — así que cada cuota es su propio evento, nunca el
  // `importe_total` completo puesto en un solo mes (a diferencia de `mes_cierre`, que sí es un
  // valor único por contrato — ver `mappedContratos`, `cargarOportunidadesVentas.js`).
  //
  // "Eventos de facturación": para `sales`/`sales_btl` (type !== "contrato"), UN evento por op si
  // `mes_factura` cae en el período; para `sales_contratos`, un evento POR CUOTA de `op.meses`
  // (spread crudo del row, disponible en el objeto — `mappedContratos` hace `{...c, ...}`) si
  // `cuota.mes` cae en el período. Solo cuenta lo YA cerrado (`esCierre`) — no hay facturación de
  // algo que no se ganó. El monto de cada cuota se convierte con el MISMO `getImporteLocal` de
  // siempre, pasándole un objeto sintético `{moneda_dolar: <del contrato>, importe: <de la cuota>,
  // facturafecha: <de la cuota>}` — `getRateForOpp` prioriza `facturafecha` sobre `mes_cierre`
  // (`calculateVentas.js`), así que el TC usado es el de la fecha real de ESA cuota, no el del
  // contrato entero (10.185).
  // `mesesSetTarget` parametrizado (2026-09-24, 17ª sesión — antes cerraba sobre `mesesSet` fijo)
  // para poder correr la MISMA función dos veces, período real y período AA, sin duplicar la
  // lógica de qué es un "evento de facturación" (10.1) — el criterio de cobertura de `mes_factura`
  // vs. `facturafecha` y el desglose por cuota de `sales_contratos` no cambian, solo el filtro de
  // mes.
  const construirEventosFacturacion = (mesesSetTarget) => {
    const eventos = [];
    oportunidadesFiltradas.forEach((op) => {
      if (!esCierre(op)) return;
      if (op.type === "contrato") {
        (op.meses || []).forEach((cuota) => {
          if (!cuota?.mes || !mesesSetTarget.has(cuota.mes)) return;
          // `pais: op.pais` en el objeto sintético — necesario para que `getImporteLocal` (cuando
          // es `getImporteUSD`) elija la tabla de cambio correcta; el evento PUSHEADO ya no lo
          // necesita (`montoPorPais` se retiró, 20ª sesión, ver comentario en
          // `construirCeldasDesdeEventos`).
          const monto = getImporteLocal({
            moneda_dolar: op.moneda_dolar,
            importe: cuota.importe,
            facturafecha: cuota.facturafecha,
            pais: op.pais,
          });
          eventos.push({ client_id: op.client_id, mes: cuota.mes, monto });
        });
      } else {
        if (!op.mes_factura || !mesesSetTarget.has(op.mes_factura)) return;
        eventos.push({ client_id: op.client_id, mes: op.mes_factura, monto: getImporteLocal(op) });
      }
    });
    return eventos;
  };

  // Bucketea una lista de eventos de facturación (ya con `mes`/`monto`) por fila, resolviendo
  // empresa/categoría — extraído para no repetirlo entre el período real y el AA (2026-09-24, 17ª
  // sesión).
  const bucketearEventosFacturacionPorFila = (eventos) => {
    const porFila = new Map(ordenFilasBase.map((f) => [f, []]));
    eventos.forEach((ev) => {
      const clienteRow = clientesPorId.get(ev.client_id);
      if (!clienteRow) return;
      const categoriaNombre = categoriasPorId.get(clienteRow.categoria) || null;
      const empresaNorm = normalizarEmpresa(clienteRow.empresa);
      const empresaDisplay = (clienteRow.empresa || "").trim();
      resolverFilasDeClienteRow(categoriaNombre, categoriasActivasSet).forEach((filaKey) =>
        porFila.get(filaKey).push({ ...ev, empresaNorm, empresaDisplay })
      );
    });
    return porFila;
  };

  const eventosFacturacionPorFila = bucketearEventosFacturacionPorFila(construirEventosFacturacion(mesesSet));
  // Año Anterior de Facturación (17ª sesión, punto 3: "cada una con su propio dato") — MISMO
  // mecanismo, filtrado contra `mesesSetAA` en vez de `mesesSet`.
  const eventosFacturacionAAPorFila = bucketearEventosFacturacionPorFila(construirEventosFacturacion(mesesSetAA));

  // Misma forma que `armarFila` (celdas con `.empresas` para el tooltip, `totalPeriodo`) pero
  // `total`/`empresasActivadas`/`enfriada`/`ultimoAnoCierre` se REUSAN de `filasArmadasPorKey` —
  // no son cosas que cambien con la vista (punto 2: "las cuatro columnas ordenables son las mismas
  // en las tres posiciones" — mismo campo por debajo en las 3, el orden nunca se desarma al
  // cambiar de posición, igual razonamiento que unificó Cobertura en la 7ª sesión).
  const armarFilaFacturacion = (filaKey) => {
    const eventosDeLaFila = eventosFacturacionPorFila.get(filaKey);
    const celdasPorMes = new Map(mesesDelPeriodo.map((m) => [m, 0]));
    const empresasPorMes = new Map(mesesDelPeriodo.map((m) => [m, new Map()]));
    eventosDeLaFila.forEach(({ mes, monto, empresaNorm, empresaDisplay }) => {
      celdasPorMes.set(mes, (celdasPorMes.get(mes) || 0) + monto);
      if (empresaNorm) {
        const mapaDelMes = empresasPorMes.get(mes);
        const prev = mapaDelMes.get(empresaNorm) || { empresaNorm, empresaDisplay, monto: 0 };
        prev.monto += monto;
        mapaDelMes.set(empresaNorm, prev);
      }
    });
    const celdas = mesesDelPeriodo.map((mes) => ({
      mes,
      monto: celdasPorMes.get(mes) || 0,
      empresas: [...empresasPorMes.get(mes).values()].sort((a, b) => b.monto - a.monto),
    }));
    const totalPeriodo = celdas.reduce((acc, c) => acc + c.monto, 0);
    const { total, empresasActivadas, enfriada, ultimoAnoCierre } = filasArmadasPorKey.get(filaKey);

    // Año Anterior de Facturación (2026-09-24, 17ª sesión) — propio, NO reusado de Cierres (punto
    // 3 del pedido de Ignacio: "cada una con su propio dato").
    const { celdas: celdasAA, totalPeriodo: totalPeriodoAA } = construirCeldasDesdeEventos(
      eventosFacturacionAAPorFila.get(filaKey),
      mesesDelPeriodoAA
    );
    const variacionPct = calcularVariacionPct(totalPeriodo, totalPeriodoAA);

    return {
      filaKey,
      label: labelDeFila(filaKey),
      celdas,
      totalPeriodo,
      total,
      empresasActivadas,
      enfriada,
      ultimoAnoCierre,
      celdasAA,
      totalPeriodoAA,
      variacionPct,
    };
  };

  const filasFacturacionPorKey = new Map(ordenFilasBase.map((filaKey) => [filaKey, armarFilaFacturacion(filaKey)]));
  // Mismo orden que Evolución — no un orden propio por `totalPeriodo` facturado (mismo mecanismo
  // que ya usa Cobertura arriba, ver comentario ahí).
  const filasFacturacion = ordenFilas.map((filaKey) => filasFacturacionPorKey.get(filaKey)).filter(Boolean);

  // Filtro de CATEGORÍA (2026-09-23, 6ª sesión, punto 3 del pedido de Ignacio): "se muestran solo
  // esas filas, nada más" — se aplica al FINAL, sobre las filas ya armadas y ordenadas, sin volver
  // a calcular nada. "Otros" nunca aparece entre las opciones del filtro (no es una categoría
  // real), así que desaparece solo apenas hay algún filtro de categoría activo — coherente con
  // "nada más".
  const filtroCategoriasSet = new Set(filtroCategorias);
  const aplicarFiltroCategoria = (filas) =>
    filtroCategoriasSet.size === 0 ? filas : filas.filter((f) => filtroCategoriasSet.has(f.filaKey));

  return {
    filasEvolucion: aplicarFiltroCategoria(filasEvolucion),
    filasFacturacion: aplicarFiltroCategoria(filasFacturacion),
    filasCobertura: aplicarFiltroCategoria(filasCobertura),
    categoriasDisponibles,
  };
};
